import type { Viewport } from '../../services/exploreApi';

/**
 * The map engine's bounds are UNBOUNDED; the server's are not.
 *
 * `map.getBounds()` reports raw world coordinates (with world copies on), so panning west across the
 * antimeridian yields `west = -190` and zooming out far enough yields a span
 * over 360°. `_parse_bbox` refuses both outright — "bbox longitudes must be
 * within -180..180" — so every request 400s, the status line reads "Could not
 * refresh this list", and "Try again" re-sends the same rejected box forever
 * while the tiles underneath render perfectly. It is reachable from the default
 * zoom control: a few clicks of "−" is all it takes.
 *
 * The server DOES handle the antimeridian, but it expects the NORMALIZED form —
 * a wrapped viewport is `west > east` (`explore_views.py`), which the engine's raw
 * coordinates never produce. So this is not merely a clamp to keep the server
 * happy; it is the translation into the representation the server's wrap
 * handling is written against.
 */
export const WHOLE_WORLD = 360;

/**
 * Zoom crosses the engine boundary here and nowhere else (MapLibre plan D7).
 * The server's scale is a 256-px world at zoom 0 (`explore_views.py`); the
 * engine's is 512 px, so server zoom = engine zoom + 1. Fractional zooms pass
 * through unrounded: the server parses a float.
 */
export const toServerZoom = (engineZoom: number): number => engineZoom + 1;
export const toEngineZoom = (serverZoom: number): number => serverZoom - 1;

/** Into [-180, 180). Not `%` alone: JS `%` keeps the sign of the dividend, so
 *  `-190 % 360` is `-190` and the value would come back unchanged. */
const wrapLng = (x: number): number => ((((x + 180) % 360) + 360) % 360) - 180;

/** The server refuses a zero-height box, and clamping can PRODUCE one: a
 *  viewport wholly past a pole (south 91, north 92) collapses to 90, 90. */
const MIN_SPAN = 1e-6;

/** The server's floor is `MIN_BBOX_SPAN` = 1e-9, and this is deliberately
 *  wider. Nudging by exactly the floor produced spans like 9.9998e-10 — the
 *  float sum rounds DOWN at these magnitudes, landing just under the limit the
 *  nudge existed to clear. A micro-degree is still a sub-metre viewport. */
const MIN_LNG_SPAN = 1e-6;

/** The span the SERVER measures, which is not `east - west`. A wrapped
 *  viewport is `west > east` and its width runs the long way round the
 *  meridian (`explore_geometry.span_x`). Measuring it with `Math.abs` called a
 *  5e-10 sliver straddling the antimeridian "nearly 360 degrees wide" and let
 *  it through to be refused by the server. */
const wrappedSpan = (west: number, east: number): number =>
  (east >= west ? east - west : east + 360 - west);

/** `null` when there is nothing sensible to ask about.
 *
 *  A map whose container has no size can report NaN bounds — hidden,
 *  zero-height, or read before it is positioned — and NaN propagates silently
 *  through every comparison here: `NaN >= x` is false, so each guard declines
 *  to fire and the NaN reaches the wire. Python's `float('NaN')` then PARSES,
 *  so `_parse_bbox` gets past its conversion and fails the range check, and
 *  the client retries the same unanswerable box forever. That is the same
 *  rejected-box loop an earlier round found for out-of-range longitudes; the
 *  range case was fixed and this one was left.
 *
 *  Asking nothing is strictly better than asking something that cannot be
 *  answered: the map keeps whatever it last drew, and the next real `moveend`
 *  reports normally. */
export const normalizeViewport = (v: Viewport): Viewport | null => {
  if (!Number.isFinite(v.west) || !Number.isFinite(v.east)
      || !Number.isFinite(v.south) || !Number.isFinite(v.north)) {
    return null;
  }
  // Clamped BEFORE wrapping. An engine can report latitudes past the poles once
  // the map is taller than the world.
  let south = Math.max(-90, Math.min(90, v.south));
  let north = Math.max(-90, Math.min(90, v.north));
  if (north - south < MIN_SPAN) {
    // Collapsed by the clamp, or degenerate to begin with. Widen INTO the
    // world rather than sending a box the server will refuse.
    if (north >= 90) south = Math.min(south, 90 - MIN_SPAN);
    else north = Math.max(north, south + MIN_SPAN);
    south = Math.max(-90, Math.min(south, north - MIN_SPAN));
  }

  // A span at OR NEAR 360 is the whole world, decided before any wrapping.
  //
  // This deliberately reverses an earlier round's rule, and the reasoning
  // matters. That round observed that -180..179.99999999999997 subtracts to
  // exactly 360 in float64, and concluded the whole-world case must be decided
  // from the exact endpoints so a viewport EXCLUDING +180 could never claim
  // everything. True, and it made the common case catastrophic: with a strict
  // `>`, a genuine whole-world viewport that is not centred on the meridian —
  // west -170, east 190, which is what a world-width map pane actually reports
  // — fell through to the wrap, where both edges land on the SAME meridian and
  // the span collapses to zero. The nudge below then emitted a 1e-9 sliver:
  // the widest possible viewport arriving as the narrowest, which is verbatim
  // the failure the previous comment claimed to prevent.
  //
  // Weigh the two errors. Over-claiming by the float noise the old rule feared
  // shows the reader about three nanometres more longitude than they asked
  // for. Under-claiming shows them an empty list under a map of the entire
  // world, and it happens at ordinary zoom levels. So: near-360 wins.
  if (v.east - v.west >= WHOLE_WORLD - MIN_LNG_SPAN) {
    return { ...v, west: -180, east: 180, south, north };
  }

  const west = wrapLng(v.west);
  let east = wrapLng(v.east);
  // Below the server's floor — either because the wrap put both edges on one
  // meridian, or because the viewport really is that small. `east` moves, not
  // `west`: `west` names which side of the meridian the box starts on.
  //
  // Always FORWARD, then wrapped back into range. Stepping backward when the
  // nudge would pass +180 looks like it keeps the value legal, and instead
  // puts `east` just under `west` — which the server reads as a wrap running
  // the long way round, turning a sub-metre sliver into a near-global
  // viewport. Forward-and-wrap expresses the same tiny box as a legitimate
  // antimeridian crossing.
  if (wrappedSpan(west, east) < MIN_LNG_SPAN) {
    east = wrapLng(west + MIN_LNG_SPAN);
  }
  return { ...v, west, east, south, north };
};
